import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { SendIcon, XIcon } from 'lucide-react';
import { ChatMessage } from '../types';
import { fallbackAnswer, knowledgeBase, quickPrompts } from '../data/knowledge';
import { company } from '../data/company';
import { quickOrderMailto, whatsappLink } from '../utils/contact';
let idSeq = 0;
const nextId = () => `m${idSeq += 1}`;
function answerFor(input: string): {
  text: string;
  suggestions: string[];
} {
  const text = input.toLowerCase();
  let bestScore = 0;
  let bestIndex = -1;
  knowledgeBase.forEach((entry, index) => {
    const score = entry.keywords.reduce((acc, kw) => text.includes(kw) ? acc + kw.length : acc, 0);
    if (score > bestScore) {
      bestScore = score;
      bestIndex = index;
    }
  });
  if (bestIndex < 0) return {
    text: fallbackAnswer,
    suggestions: quickPrompts.slice(0, 3)
  };
  const entry = knowledgeBase[bestIndex];
  return {
    text: entry.answer,
    suggestions: entry.suggestions ?? []
  };
}
interface Props {
  open: boolean;
  onClose: () => void;
}
export function ChatAgent({
  open,
  onClose
}: Props) {
  const [messages, setMessages] = useState<ChatMessage[]>([{
    id: nextId(),
    role: 'agent',
    text: `Welcome to ${company.shortName}. I am Kito, the trading desk assistant. Ask me about specifications, minimum quantities, pricing basis, documentation or logistics — or tell me what you would like to order.`
  }]);
  const [suggestions, setSuggestions] = useState<string[]>(quickPrompts.slice(0, 3));
  const [draft, setDraft] = useState('');
  const [thinking, setThinking] = useState(false);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const timers = useRef<number[]>([]);
  useEffect(() => () => {
    timers.current.forEach(t => window.clearTimeout(t));
  }, []);
  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, thinking]);
  const send = (raw: string) => {
    const value = raw.trim();
    if (!value || thinking) return;
    setMessages(prev => [...prev, {
      id: nextId(),
      role: 'user',
      text: value
    }]);
    setDraft('');
    setSuggestions([]);
    setThinking(true);
    const reply = answerFor(value);
    const t = window.setTimeout(() => {
      setMessages(prev => [...prev, {
        id: nextId(),
        role: 'agent',
        text: reply.text
      }]);
      setSuggestions(reply.suggestions);
      setThinking(false);
    }, 750);
    timers.current.push(t);
  };
  const onlineLabel = useMemo(() => `Online · replies in seconds`, []);
  return <AnimatePresence data-id="element-18" data-mp="2evfyeq6cjjfb">
      {open && <motion.aside role="dialog" aria-label="Customer help centre chat" initial={{
      opacity: 0,
      y: 16,
      scale: 0.97
    }} animate={{
      opacity: 1,
      y: 0,
      scale: 1
    }} exit={{
      opacity: 0,
      y: 12,
      scale: 0.98
    }} transition={{
      duration: 0.22,
      ease: [0.23, 1, 0.32, 1]
    }} className="fixed bottom-24 right-5 z-50 flex h-[540px] w-[calc(100vw-2.5rem)] max-w-sm flex-col overflow-hidden rounded-sm border border-hair bg-panel shadow-2xl sm:right-6" data-id="element-19" data-mp="flhl64j3g053">
          <div className="flex items-start justify-between gap-3 border-b border-hair bg-panel2 px-4 py-3.5" data-id="element-20" data-mp="3ui2qtyxpo89e">
            <div className="flex items-center gap-3" data-id="element-21" data-mp="vym2uen6v4li">
              <img src={company.logo} alt="" className="h-9 w-9 shrink-0 object-contain" data-id="element-22" data-mp="r0avcakd966g" />
              <div data-id="element-23" data-mp="m0ofruh2fduh">
                <p className="text-sm font-semibold text-bone" data-id="element-24" data-mp="m06nta1frlsd">Kito — Help Centre</p>
                <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-emerald-300/80" data-id="element-25" data-mp="hzmmrvdqry0y">
                  {onlineLabel}
                </p>
              </div>
            </div>
            <button type="button" onClick={onClose} aria-label="Close chat" className="rounded-sm p-1.5 text-muted transition-colors duration-150 hover:text-bone" data-id="element-26" data-mp="32ptwp1pdfrzz">
              <XIcon className="h-4 w-4" data-id="element-27" data-mp="z8nlqwis0jha" />
            </button>
          </div>

          <div ref={scrollRef} className="thin-scroll flex-1 space-y-3 overflow-y-auto px-4 py-4" data-id="element-28" data-mp="kj7y3pejku5t">
            {messages.map(message => <div key={message.id} className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`} data-id="element-29" data-mp="pica0nsup5zu">
                <p className={`max-w-[85%] rounded-sm px-3.5 py-2.5 text-[13px] leading-relaxed ${message.role === 'user' ? 'bg-gold text-ink' : 'border border-hair bg-panel2 text-bone/90'}`} data-id="element-30" data-mp="1i6q8f4nuq4bx">
                  {message.text}
                </p>
              </div>)}

            {thinking && <div className="flex justify-start" data-id="element-31" data-mp="3t02qgaxpqw5n">
                <span className="flex items-center gap-1 rounded-sm border border-hair bg-panel2 px-3.5 py-3" data-id="element-32" data-mp="3leqj9bhtc01b">
                  {[0, 1, 2].map(i => <motion.span key={i} className="h-1.5 w-1.5 rounded-full bg-gold" animate={{
              opacity: [0.25, 1, 0.25]
            }} transition={{
              duration: 0.9,
              repeat: Infinity,
              delay: i * 0.15
            }} data-id="element-33" data-mp="3gfnpin7a9g8n" />)}
                </span>
              </div>}

            {!thinking && suggestions.length > 0 && <div className="flex flex-wrap gap-2 pt-1" data-id="element-34" data-mp="3cebv84dgs6u4">
                {suggestions.map(s => <button key={s} type="button" onClick={() => send(s)} className="rounded-sm border border-hair px-2.5 py-1.5 text-left text-[11px] text-muted transition-colors duration-150 hover:border-gold hover:text-gold-light" data-id="element-35" data-mp="37nvhej5qej78">
                    {s}
                  </button>)}
              </div>}
          </div>

          <div className="border-t border-hair bg-panel2 px-4 py-3" data-id="element-36" data-mp="38n44duc7k0em">
            <form onSubmit={e => {
          e.preventDefault();
          send(draft);
        }} className="flex items-center gap-2" data-id="element-37" data-mp="3df3y351opx2r">
              <label className="sr-only" htmlFor="chat-input" data-id="element-38" data-mp="2lozhzikyjw7c">
                Message the help centre
              </label>
              <input id="chat-input" ref={inputRef} value={draft} onChange={e => setDraft(e.target.value)} placeholder="Ask about grades, quantities, terms…" className="min-w-0 flex-1 rounded-sm border border-hair bg-ink px-3 py-2.5 text-[13px] text-bone placeholder:text-muted/70 focus:border-gold focus:outline-none" data-id="element-39" data-mp="2pqkrqv273udb" />
              <button type="submit" aria-label="Send message" className="rounded-sm bg-gold p-2.5 text-ink transition-colors duration-150 hover:bg-gold-light disabled:opacity-50" disabled={!draft.trim() || thinking} data-id="element-40" data-mp="ezkx4hgv2541">
                <SendIcon className="h-4 w-4" data-id="element-41" data-mp="1pnwitod88zk4" />
              </button>
            </form>
            <div className="mt-2.5 flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.14em]" data-id="element-42" data-mp="1kqml7lfophl7">
              <a href={whatsappLink()} target="_blank" rel="noreferrer" className="text-emerald-300/90 transition-colors duration-150 hover:text-emerald-200" data-id="element-43" data-mp="1bq7v88rrzebd">
                Talk to a trader on WhatsApp
              </a>
              <a href={quickOrderMailto()} className="text-gold transition-colors duration-150 hover:text-gold-light" data-id="element-44" data-mp="3155x7whi11ek">
                Place order
              </a>
            </div>
          </div>
        </motion.aside>}
    </AnimatePresence>;
}